import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Box, HStack, Image, Text, useColorModeValue } from "@chakra-ui/react";
import { AppShell, Header, Sidebar, useAppShell } from "./index";
import { getNavItems } from "./navItems";
import { useAuth } from "../../hooks/useAuth";
import { useUser } from "../../Context/userContext";
import { AdminTenantSearch, ProfileMenu } from "../app";

/**
 * Header height, shared between the `<Header>` slot and `<AppShell
 * headerHeight>` — must match (AppShell has no way to introspect the
 * header's own rendered height). Shrunk from Chakra's 64px default as part
 * of the general chrome-density pass (see UI_LAYOUT_DENSITY_PLAN.md §5.5).
 */
const HEADER_HEIGHT = "48px";

/** Routes dense enough (table + filters, see the plan's §H5/H6) to need the
 * full-viewport contained-scroll layout instead of normal page scroll. */
const DENSE_TABLE_ROUTES = ["/transactions", "/snapshots"];

/**
 * Logo component (Internal for Layout)
 */
const Logo: React.FC = () => {
  const logoSrc = useColorModeValue("/logo-light.png", "/logo-darj.png");
  const textColor = useColorModeValue("xacto.deepNavy", "xacto.electricCyan");

  return (
    <HStack spacing={2} cursor="pointer">
      <Image src={logoSrc} alt="X-acto logo" h="36px" w="36px" objectFit="contain" />
      <Text
        fontWeight="bold"
        fontSize="lg"
        color={textColor}
        display={{ base: "none", md: "block" }}
      >
        X-acto
      </Text>
    </HStack>
  );
};

/**
 * Header wired to AppShell's context. Split out from AppLayout because
 * `useAppShell()` can only be called from a component rendered *inside*
 * `<AppShell>` (it reads from the context AppShell provides) — AppLayout
 * itself renders the `<AppShell>` element, so it's one level too high.
 *
 * Passing `onMenuClick` here is what makes the mobile hamburger button
 * appear at all: `Header` only renders it when it has a handler.
 */
const LayoutHeader: React.FC = () => {
  const navigate = useNavigate();
  const { toggleSidebar } = useAppShell();

  return (
    <Header
      logo={
        <Box onClick={() => navigate("/home")}>
          <Logo />
        </Box>
      }
      title="Financial Dashboard"
      height={HEADER_HEIGHT}
      onMenuClick={toggleSidebar}
      actions={<AdminTenantSearch />}
      userMenu={<ProfileMenu />}
    />
  );
};

interface LayoutSidebarProps {
  navItems: ReturnType<typeof getNavItems>;
  currentPath: string;
  onNavigate: (path: string) => void;
}

/** Sidebar wired to AppShell's collapse state — same reasoning as LayoutHeader. */
const LayoutSidebar: React.FC<LayoutSidebarProps> = ({ navItems, currentPath, onNavigate }) => {
  const { isSidebarCollapsed, toggleSidebarCollapse, isMobile } = useAppShell();

  return (
    <Sidebar
      items={navItems}
      currentPath={currentPath}
      onNavigate={onNavigate}
      isCollapsed={isSidebarCollapsed && !isMobile}
      onToggleCollapse={isMobile ? undefined : toggleSidebarCollapse}
    />
  );
};

export const AppLayout: React.FC = () => {
  const { user } = useAuth();
  const { user: userProfile, selectedTenant } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is admin or tenant admin
  // If userProfile is null, default to false (non-admin)
  // The navItems will update when userProfile loads
  const isAdmin = userProfile?.globalRole === "ADMIN";
  const isTenantAdmin =
    isAdmin || selectedTenant?.isOwner || selectedTenant?.tenantRole === "TENANT_ADMIN";

  // Navigation Items
  const navItems = getNavItems(isAdmin, isTenantAdmin);

  // Handle Navigation
  const handleNavigate = (path: string) => {
    navigate(path);
  };

  // Determine active path for sidebar highlighting
  const currentPath = location.pathname;

  return (
    <AppShell
      header={<LayoutHeader />}
      headerHeight={HEADER_HEIGHT}
      fullHeight={DENSE_TABLE_ROUTES.includes(currentPath)}
      sidebar={<LayoutSidebar navItems={navItems} currentPath={currentPath} onNavigate={handleNavigate} />}
    >
      <Outlet />
    </AppShell>
  );
};
